import { ContentLink } from "@components/cms/link/ContentLink";
import { H } from "@ecco/ui/a11y-heading";
import { Text } from "@ecco/ui/text";
import { Button } from "@ecco/ui/button";
import { Card } from "@ecco/ui/card";
import { Flex } from "@ecco/ui/flex";
import { ECCOIcon } from "@ecco/ui/ecco-icon";
import type {
    CardLinkCollectionModel,
    CardLinkModel,
} from "@lib/contentful/mappers/modules/cardLinkCollection/types";
import type { FC } from "react";

type LinksProps = { links: CardLinkModel[] };

const TextLinks: FC<LinksProps> = ({ links }) => (
    <Flex wrap="wrap" gap="8" mt={6}>
        {links.map(({ link, description }) => (
            <Flex
                key={link.id}
                as={ContentLink}
                link={link}
                direction="column"
                gap={2}
                maxW={64}
                minW={64}
                textDecoration="none"
                _hover={{
                    textDecoration: "none",
                }}
            >
                <Flex gap={3} alignItems="center">
                    {link.label && (
                        <Text
                            fontWeight="semibold"
                            fontSize={[
                                "mobileBodyTextNormal",
                                "mobileBodyTextNormal",
                                "desktopBodyTextNormal",
                            ]}
                        >
                            {link.label}
                        </Text>
                    )}
                    <ECCOIcon name="arrow-right" size="2xs" />
                </Flex>
                {description && (
                    <Text
                        fontWeight="semibold"
                        fontSize={[
                            "mobileBodyTextSmall",
                            "mobileBodyTextSmall",
                            "desktopBodyTextSmall",
                        ]}
                        color="black"
                        maxW="80%"
                    >
                        {description}
                    </Text>
                )}
            </Flex>
        ))}
    </Flex>
);

const ButtonLinks: FC<LinksProps> = ({ links }) => (
    <Flex wrap="wrap" gap="4" mt={6}>
        {links.map(({ link }) => (
            <Button
                key={link.id}
                as={ContentLink}
                link={link}
                variant="tertiary"
                textDecoration="none"
                _hover={{
                    textDecoration: "none",
                }}
                px={6}
                py={4}
            >
                <span>{link.label}</span>
            </Button>
        ))}
    </Flex>
);

const linksMap = {
    text: TextLinks,
    button: ButtonLinks,
} as const;

export const CardLinkCollection: FC<CardLinkCollectionModel> = ({
    header,
    description,
    style,
    links,
}) => {
    const Links = linksMap[style];

    return (
        <Card gap={0}>
            {header && <H size="h5">{header}</H>}
            {description && (
                <Text mt={4} color="black" fontWeight="semibold">
                    {description}
                </Text>
            )}
            {links.length > 0 && <Links links={links} />}
        </Card>
    );
};
